import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';

import {useSelector} from 'react-redux';
import {Navigate} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {selectActiveBranchId} from '@dms/api/features';
import {
  SalesLeaguePeriod,
  useGetSalesLeagueBranchBoardQuery,
  useLazyExportSalesLeagueBranchBoardCsvQuery,
} from '@dms/api/sales';
import {restrictedAreaRoutes, testIds} from '@dms/routes';
import {handleApiError, Main} from '@dms/shared';

import {useQueryState} from 'shared/web';

import {UNRESOLVED_BRANCH_ID} from '../../constants/SALES_LEAGUE_BRANCH_ID_QUERY_PARAM';
import {SALES_LEAGUE_PERIOD_QUERY_PARAM} from '../../constants/SALES_LEAGUE_PERIODS';
import {downloadSalesLeagueCsv} from '../../utils/downloadSalesLeagueCsv';
import {parseSalesLeaguePeriod} from '../../utils/parseSalesLeaguePeriod';
import {SalesLeagueBranchBoardView} from './components/SalesLeagueBranchBoardView';

const PAGE_MIN_HEIGHT = 100;
const FORBIDDEN_STATUS = 403;

export function SalesLeagueBranchBoard() {
  // The period lives in the URL so a board can be linked and shared.
  const [rawPeriod, setRawPeriod] = useQueryState(SALES_LEAGUE_PERIOD_QUERY_PARAM, true);
  const period = parseSalesLeaguePeriod(rawPeriod);

  const activeBranchId = useSelector(selectActiveBranchId);
  const resolvedActiveBranchId =
    isNotNil(activeBranchId) && activeBranchId !== UNRESOLVED_BRANCH_ID
      ? activeBranchId
      : undefined;

  const {data, isLoading, isError, error} = useGetSalesLeagueBranchBoardQuery(
    isNotNil(resolvedActiveBranchId) ? {period, activeBranchId: resolvedActiveBranchId} : skipToken
  );

  const [exportCsv, {isFetching: isExporting}] = useLazyExportSalesLeagueBranchBoardCsvQuery();

  const isForbidden = isNotNil(error) && 'status' in error && error.status === FORBIDDEN_STATUS;

  const handlePeriodChange = (nextPeriod: SalesLeaguePeriod) => setRawPeriod(nextPeriod);

  // The board is always the whole tenant, so the export takes the period and nothing else.
  const handleExport = () =>
    exportCsv({period}).unwrap().then(downloadSalesLeagueCsv).catch(handleApiError);

  if (isForbidden) {
    return <Navigate to={restrictedAreaRoutes.home} replace />;
  }

  return (
    <Main data-testid={testIds.salesLeague.branchBoard('page')}>
      {/* Loading is rendered as a skeleton board rather than a spinner, so the period
          switcher stays usable and the layout does not jump when the data lands. */}
      <DataStatus isError={isError} minHeight={PAGE_MIN_HEIGHT}>
        <SalesLeagueBranchBoardView
          branchBoard={data}
          isLoading={isLoading || isNil(resolvedActiveBranchId)}
          period={period}
          onPeriodChange={handlePeriodChange}
          onExport={handleExport}
          isExporting={isExporting}
          data-testid={testIds.salesLeague.branchBoard('board')}
        />
      </DataStatus>
    </Main>
  );
}
